import isFunction from 'lodash/isFunction.js';
import PendoTooltip from '../../directives/tooltip/pendo-tooltip.js';
import SkeletonLoader from '../../directives/loading/components/skeleton.vue.js';
import Checkbox from './cell-types/pendo-checkbox-cell.vue.js';
import Tree from './cell-types/tree.vue.js';
import Link from './cell-types/link.vue.js';
import { getProp } from './utils/utils.js';
import { capitalize } from '../../utils/utils.js';
import { findNodeByContent, elementHasOverflow } from '../../utils/dom.js';

PendoTooltip.options.disposeTimeout = 0;
var TableCell = {
  name: 'TableCell',
  inject: ['$table'],
  components: {
    Checkbox,
    Link,
    SkeletonLoader,
    Tree
  },
  directives: {
    PendoTooltip
  },
  props: {
    column: {
      type: Object
    },
    columnIndex: {
      type: Number
    },
    naturalIndex: {
      type: Number
    },
    row: {
      type: Object
    },
    rowIndex: {
      type: Number
    },
    rowLevel: {
      type: Number
    }
  },
  data() {
    return {
      tooltipContent: null
    };
  },
  computed: {
    fixedColumnStyle() {
      const style = {};
      if (this.$table.overflowX) {
        if (this.column.fixed === 'left') {
          style.left = `${this.column.fixedPos}px`;
        }
        if (this.column.fixed === 'right') {
          style.right = `${this.column.fixedPos}px`;
        }
      }
      if (this.row.height) {
        style.height = `${this.row.height}px`;
      }
      return style;
    },
    cellClassName() {
      if (this.$table.cellClassName) {
        return isFunction(this.$table.cellClassName) ? this.$table.cellClassName({
          row: this.row,
          rowIndex: this.rowIndex,
          column: this.column,
          columnIndex: this.columnIndex
        }) : this.$table.cellClassName;
      }
      return '';
    },
    cellContentStyle() {
      const style = {};
      const useProvidedRowHeight = !this.$table.scrollStore.rowHeight && this.row.height;
      if (useProvidedRowHeight) {
        style.maxHeight = `${this.row.height}px`;
      }
      if (this.$table.border) {
        style.width = `${this.column.renderWidth - 1}px`;
        return style;
      }
      style.width = `${this.column.renderWidth}px`;
      return style;
    },
    cellContent() {
      if (this.column.formatter) {
        return this.column.formatter(this.row, this.column);
      }
      const rowValue = getProp(this.row, this.column.property);
      if (rowValue === 0) {
        return rowValue;
      }
      if (typeof rowValue === 'boolean') {
        return String(rowValue);
      }
      return rowValue || '';
    },
    listenForClickEvents() {
      if (['index', 'checkbox', 'tree'].includes(this.column.type)) {
        return false;
      }
      return this.$table.highlightCurrentRow || Boolean(this.$table.$listeners['cell-click']);
    },
    listenForHoverEvents() {
      if (this.$table.scrollStore.scrollActive) {
        return false;
      }
      return this.showTooltip;
    },
    showClamp() {
      return this.column.showOverflow === 'clamp';
    },
    showEllipsis() {
      return (this.column.showOverflow || this.$table.showAllOverflow) === 'ellipsis';
    },
    showTitle() {
      return (this.column.showOverflow || this.$table.showAllOverflow) === 'title';
    },
    showTooltip() {
      if (['actions', 'index', 'tree'].includes(this.column.type)) {
        return false;
      }
      return this.column.showOverflow === true || this.column.showOverflow === 'clamp' || this.column.showOverflow === 'tooltip' || this.$table.showAllOverflow === true || this.$table.showAllOverflow === 'clamp' || this.$table.showAllOverflow === 'tooltip';
    },
    scrollActive() {
      return this.$table.scrollStore.scrollActive;
    },
    showLoading() {
      if (!this.column.async) {
        return false;
      }
      if (this.cellContent === 0) {
        return false;
      }
      return !this.cellContent;
    },
    showTreeExpand() {
      return this.$table.treeConfig && this.row[this.$table.treeConfig.children];
    }
  },
  watch: {
    scrollActive(active) {
      if (active) {
        this.tooltipContent = null;
      }
    }
  },
  methods: {
    onCellClick(event) {
      this.$table.handleCellClick(event, {
        row: this.row,
        column: this.column,
        cell: event.currentTarget
      });
    },
    onCellHover(_ref) {
      let {
        currentTarget
      } = _ref;
      this.tooltipContent = null;
      if (this.column.tooltipFormatter) {
        const customTooltipContent = this.column.tooltipFormatter(this.row, this.column);
        if (customTooltipContent) {
          this.tooltipContent = customTooltipContent;
        }
        return;
      }
      const cellContentNode = findNodeByContent(this.cellContent, currentTarget);
      if (!cellContentNode.length) {
        return;
      }
      if (elementHasOverflow(cellContentNode[0])) {
        requestAnimationFrame(() => {
          this.tooltipContent = this.cellContent;
        });
      }
    },
    onCheckboxChange() {
      this.$table.toggleRowSelect({
        row: this.row
      });
    }
  },
  render(createElement) {
    const {
      row,
      rowIndex,
      rowLevel,
      column,
      columnIndex,
      cellContent,
      naturalIndex,
      cellClassName,
      cellContentStyle,
      fixedColumnStyle,
      showTooltip,
      showClamp,
      showEllipsis,
      listenForClickEvents,
      listenForHoverEvents,
      onCellClick,
      onCellHover,
      onCheckboxChange,
      tooltipContent,
      $table,
      showLoading,
      showTreeExpand
    } = this;
    let children = cellContent;
    let isTextNode = true;
    const tooltipDirective = {
      name: 'pendo-tooltip',
      value: {
        content: tooltipContent,
        delay: {
          show: 300,
          hide: 0
        },
        arrow: true,
        multiline: true,
        html: false,
        boundariesElement: 'body'
      }
    };
    const slot = column.slot || column.type || column.property;
    if ($table.$scopedSlots && $table.$scopedSlots[slot] && !showLoading) {
      isTextNode = false;
      children = $table.$scopedSlots[slot]({
        row,
        rowIndex,
        column,
        columnIndex,
        toggleRowActive: $table.toggleRowActive
      });
    }
    if (column && column.type) {
      isTextNode = false;
      switch (column.type) {
        case 'checkbox':
          {
            children = createElement(Checkbox, {
              props: {
                checked: Boolean($table.selectedMap[row[$table.rowKey]]),
                disabled: $table.checkboxConfig.checkMethod && !$table.checkboxConfig.checkMethod(row)
              },
              on: {
                change: onCheckboxChange
              },
              directives: [tooltipDirective]
            });
            break;
          }
        case 'link':
          children = createElement(capitalize(column.type), {
            props: {
              row,
              rowLevel,
              column,
              content: children
            }
          });
          break;
        case 'tree':
          if (showTreeExpand) {
            children = createElement(Tree, {
              props: {
                row,
                rowLevel
              }
            });
          }
          break;
        case 'index':
          children = naturalIndex;
          break;
        case 'actions':
          {
            const {
              scrollActive
            } = $table.scrollStore;
            const childRow = rowLevel > 0;
            if (scrollActive || childRow) {
              children = [];
              break;
            }
            const hoverRow = row[$table.rowKey] === $table.hoverRow;
            const activeRow = row[$table.rowKey] === $table.activeRow;
            if (!hoverRow && !activeRow) {
              children = [];
              break;
            }
          }
      }
    }
    const cellEventListeners = {};
    if (listenForClickEvents) {
      cellEventListeners.click = onCellClick;
    }
    if (listenForHoverEvents) {
      cellEventListeners.mouseenter = onCellHover;
    }
    const cellDirectives = [];
    if (showTooltip && column.type !== 'checkbox') {
      cellDirectives.push(tooltipDirective);
    }
    return createElement('td', {
      class: ['pendo-table__column', cellClassName, {
        'pendo-table__column--actions': column.type === 'actions',
        'pendo-table__column--center': column.align === 'center',
        'pendo-table__column--right': column.align === 'right',
        'pendo-table__column--fixed-left': $table.overflowX && column.fixed === 'left',
        'pendo-table__column--fixed-right': $table.overflowX && column.fixed === 'right',
        'pendo-table__column--fixed-shadow': column.fixedShadow,
        'is-fixed-actions': !$table.scrollStore.scrollActive && $table.overflowX && column.type === 'actions',
        'is-fixed-left': $table.overflowX && $table.scrollLeftToRight && column.fixed === 'left',
        'is-fixed-right': $table.overflowX && $table.scrollRightToLeft && column.fixed === 'right'
      }],
      style: fixedColumnStyle,
      attrs: {
        'pendo-draggable-handle': $table.draggable,
        'role': 'gridcell',
        'aria-rowindex': naturalIndex + 1,
        'aria-colindex': columnIndex + 1
      },
      on: cellEventListeners
    }, [createElement('div', {
      class: ['pendo-table__cell', {
        'pendo-table__cell--index': column.type === 'index',
        'pendo-table__cell--text': isTextNode,
        'pendo-table__cell--tooltip': showTooltip,
        'pendo-table__cell--checkbox': column.type === 'checkbox',
        'pendo-table__cell--tree': column.type === 'tree' && showTreeExpand,
        'pendo-table__cell--ellipsis': showEllipsis,
        'pendo-table__cell--clamp': showClamp
      }],
      style: cellContentStyle,
      attrs: {
        'pendo-draggable-handle': $table.draggable && column.type !== 'link',
        'pendo-draggable-ignore': $table.draggable && column.type === 'link'
      },
      directives: cellDirectives
    }, [showLoading ? createElement(SkeletonLoader, {
      props: {
        containerWidth: Math.max(0, parseInt(column.renderWidth - 32)),
        containerHeight: 8,
        skeletonProps: {
          animate: true,
          borderRadius: 2,
          height: 8,
          primaryColor: '#eaecf1',
          secondaryColor: '#f8f8f9',
          speed: 2,
          width: Math.max(0, parseInt(column.renderWidth - 32))
        }
      }
    }) : children])]);
  }
};

export { TableCell as default };
//# sourceMappingURL=table-cell.js.map
